import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge, TreeView } from 'react-bootstrap';
import { FaList, FaCheck, FaTimes, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import permisosApi from '../../../services/api/permisosApi';
import menusApi from '../../../services/api/menusApi';
import 'react-toastify/dist/ReactToastify.css';

const MenusRoles = () => {
  const [roles, setRoles] = useState([]);
  const [menuOpciones, setMenuOpciones] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedMenuOpciones, setSelectedMenuOpciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());

  useEffect(() => {
    loadRoles();
    loadMenuOpciones();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      loadMenuOpcionesRol();
    }
  }, [selectedRole]);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await permisosApi.getRoles();
      if (response.data) {
        setRoles(Array.isArray(response.data) ? response.data : [response.data]);
      }
    } catch (error) {
      console.error('Error al cargar roles:', error);
      toast.error('Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  const loadMenuOpciones = async () => {
    try {
      const response = await menusApi.getMenuOpciones({ activo: true });
      if (response.data) {
        setMenuOpciones(Array.isArray(response.data) ? response.data : [response.data]);
      }
    } catch (error) {
      console.error('Error al cargar opciones de menú:', error);
      toast.error('Error al cargar opciones de menú');
    }
  };

  const loadMenuOpcionesRol = async () => {
    if (!selectedRole) return;
    
    setLoading(true);
    try {
      const response = await menusApi.getMenuOpcionesRol(selectedRole.id);
      if (response.data) {
        const menuOpcionIds = Array.isArray(response.data) 
          ? getAllMenuOpcionIds(response.data)
          : [response.data.id];
        setSelectedMenuOpciones(menuOpcionIds);
      } else {
        setSelectedMenuOpciones([]);
      }
    } catch (error) {
      console.error('Error al cargar opciones de menú del rol:', error);
      toast.error('Error al cargar opciones de menú del rol');
      setSelectedMenuOpciones([]);
    } finally {
      setLoading(false);
    }
  };

  const getAllMenuOpcionIds = (items) => {
    let ids = [];
    items.forEach(item => {
      ids.push(item.id);
      if (item.hijos && item.hijos.length > 0) {
        ids = ids.concat(getAllMenuOpcionIds(item.hijos));
      }
    });
    return ids;
  };

  const handleRoleChange = (e) => {
    const roleId = parseInt(e.target.value);
    const role = roles.find(r => r.id === roleId);
    setSelectedRole(role || null);
    setSelectedMenuOpciones([]);
  };

  const handleMenuOpcionToggle = (menuOpcionId) => {
    setSelectedMenuOpciones(prev => {
      if (prev.includes(menuOpcionId)) {
        return prev.filter(id => id !== menuOpcionId);
      } else {
        return [...prev, menuOpcionId];
      }
    });
  };

  const toggleExpand = (menuOpcionId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuOpcionId)) {
        newSet.delete(menuOpcionId);
      } else {
        newSet.add(menuOpcionId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!selectedRole) {
      toast.error('Seleccione un rol');
      return;
    }

    setSaving(true);
    try {
      await menusApi.asignarMenuOpcionesRol(selectedRole.id, selectedMenuOpciones);
      toast.success('Opciones de menú asignadas correctamente');
      await loadMenuOpcionesRol();
    } catch (error) {
      console.error('Error al asignar opciones de menú:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Error al asignar opciones de menú';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const renderMenuTree = (items, level = 0) => {
    if (!items || items.length === 0) return null;

    // Ordenar items por orden y luego por label
    const sortedItems = [...items].sort((a, b) => {
      // Primero por orden (si existe y no es null)
      const ordenA = a.orden !== null && a.orden !== undefined ? a.orden : 9999;
      const ordenB = b.orden !== null && b.orden !== undefined ? b.orden : 9999;
      
      if (ordenA !== ordenB) {
        return ordenA - ordenB;
      }
      
      // Si tienen el mismo orden, ordenar por label
      return (a.label || '').localeCompare(b.label || '', 'es', { numeric: true, sensitivity: 'base' });
    });

    return sortedItems.map(item => {
      const hasChildren = item.hijos && item.hijos.length > 0;
      const isExpanded = expandedItems.has(item.id);
      const isSelected = selectedMenuOpciones.includes(item.id);

      return (
        <div key={item.id} style={{ marginLeft: `${level * 20}px` }}>
          <div 
            className="d-flex align-items-center p-2 border-bottom"
            style={{ 
              backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
              cursor: 'pointer'
            }}
          >
            {hasChildren && (
              <span 
                onClick={() => toggleExpand(item.id)}
                style={{ marginRight: '8px', cursor: 'pointer' }}
              >
                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
              </span>
            )}
            {!hasChildren && <span style={{ marginRight: '24px' }} />}
            <Form.Check
              type="checkbox"
              checked={isSelected}
              onChange={() => handleMenuOpcionToggle(item.id)}
              label={item.label}
              style={{ margin: 0 }}
            />
            <Badge bg="secondary" className="ms-2">{item.tipo}</Badge>
            {item.path && (
              <Badge bg="info" className="ms-2">{item.path}</Badge>
            )}
          </div>
          {hasChildren && isExpanded && item.hijos && (
            <div>
              {renderMenuTree(item.hijos, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <Container fluid className="py-4">
      <ToastContainer />
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <FaList className="me-2" />
                Asignar Opciones de Menú a Roles
              </h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Seleccionar Rol</Form.Label>
                    <Form.Select
                      value={selectedRole?.id || ''}
                      onChange={handleRoleChange}
                      disabled={loading}
                    >
                      <option value="">-- Seleccione un rol --</option>
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.nombre} {role.activo ? '' : '(Inactivo)'}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {selectedRole && (
                    <Alert variant="info">
                      <strong>Rol seleccionado:</strong> {selectedRole.nombre}
                      {selectedRole.descripcion && (
                        <div><small>{selectedRole.descripcion}</small></div>
                      )}
                    </Alert>
                  )}
                </Col>
                <Col md={8}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Opciones de Menú</h5>
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={!selectedRole || saving}
                    >
                      {saving ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <FaCheck className="me-2" />
                          Guardar Asignaciones
                        </>
                      )}
                    </Button>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" />
                      <p className="mt-2">Cargando opciones de menú...</p>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '600px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                      {menuOpciones.length > 0 ? (
                        renderMenuTree(menuOpciones)
                      ) : (
                        <Alert variant="warning" className="m-3">
                          No hay opciones de menú disponibles
                        </Alert>
                      )}
                    </div>
                  )}

                  {selectedMenuOpciones.length > 0 && (
                    <Alert variant="success" className="mt-3">
                      <strong>{selectedMenuOpciones.length}</strong> opción(es) de menú seleccionada(s)
                    </Alert>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MenusRoles;
